/* storybook-check-ignore */
import React from 'react';
import {
  LocalDateOrLocalDateTime,
  formatInPersonEaMorningAfternoonTimeslot,
  useSSEAContext,
} from 'sellDirect/SSEAContext';
import { Flex, FlexProps, Box, H4, Image } from '@opendoor/bricks/core';
import { WrapperCard } from 'sellDirect/components/legacy/sellerDash/exteriorAssessment/WrapperCard';
import { ExteriorAssessmentLabel } from 'sellDirect/components/legacy/sellerDash/exteriorAssessment/ExteriorAssessmentLabel';
import { EarliestAvailabilityLabel } from 'sellDirect/components/legacy/sellerDash/exteriorAssessment/EarliestAvailabilityLabel';
import { OnChangeCtaWithDivider } from 'sellDirect/components/legacy/sellerDash/exteriorAssessment/OnChangeCtaWithDivider';
import { LocalDateTime, ZoneId } from '@js-joda/core';
import { FULL_DATE_AND_DAY_OF_WEEK_FORMAT } from 'shared/helpers/datetime';
import { useExperimentationContext } from 'sellDirect/ExperimentationContext';
import { useQualificationContext } from 'shared/helpers/QualificationContext';

interface IAppointmentDateCardProps extends FlexProps {
  onChange?: () => void;
  onlyShowCta?: boolean;
}

interface IAppointmentDateCardWithoutContextProps extends IAppointmentDateCardProps {
  displayTime: LocalDateOrLocalDateTime;
  hasUpcomingEA: boolean;
  isSelecting: boolean;
  shouldShowEarliestAvailableLabel: boolean;
  isInPersonSseaEnabled?: boolean;
  zoneId: ZoneId;
}

const IN_PERSON_EA_SCHEDULED_LABEL = 'Home assessment scheduled';
/** TODO: Add documentation */
export const AppointmentDateCardWithoutContext: React.FC<IAppointmentDateCardWithoutContextProps> =
  ({
    displayTime,
    hasUpcomingEA,
    isSelecting,
    onChange,
    onlyShowCta,
    shouldShowEarliestAvailableLabel,
    isInPersonSseaEnabled,
    zoneId,
    ...props
  }) => {
    const eaLabelDisplayTextOverride = isInPersonSseaEnabled
      ? IN_PERSON_EA_SCHEDULED_LABEL
      : undefined;
    const label = hasUpcomingEA ? (
      <ExteriorAssessmentLabel displayText={eaLabelDisplayTextOverride} />
    ) : shouldShowEarliestAvailableLabel ? (
      <EarliestAvailabilityLabel />
    ) : null;

    return (
      <WrapperCard onlyShowCta={onlyShowCta} {...props}>
        <Flex width="100%" alignItems="center" justifyContent="center">
          <Box mr={5}>
            <Image
              alt="logo"
              src="https://imgdrop.imgix.net/1771221cd131445e9e3e810f2fb5c4ee.png"
            />
          </Box>

          {displayTime && (
            <Flex flexDirection="column" width="100%" data-testid="header">
              {!isSelecting && label}
              <Flex flexDirection="column">
                <H4 color="brand50" fontWeight="bold">
                  {displayTime.format(FULL_DATE_AND_DAY_OF_WEEK_FORMAT)}
                </H4>
                {isInPersonSseaEnabled && (
                  <H4 color="brand50" fontWeight="bold">
                    {` from ${formatInPersonEaMorningAfternoonTimeslot(
                      displayTime as LocalDateTime,
                      zoneId,
                    )}`}
                  </H4>
                )}
              </Flex>
            </Flex>
          )}
        </Flex>

        {!onlyShowCta && <OnChangeCtaWithDivider onChange={onChange} />}
      </WrapperCard>
    );
  };

/** TODO: Add documentation */
export const AppointmentDateCard: React.FC<IAppointmentDateCardProps> = (props) => {
  const { selectedDate, earliestSlot, currentInspectionDate, hasUpcomingEA } = useSSEAContext();
  const { zoneId } = useQualificationContext();

  const isSelecting = !!selectedDate && !selectedDate.equals(currentInspectionDate);

  const displayTime = isSelecting ? selectedDate : currentInspectionDate || earliestSlot;
  const shouldShowEarliestAvailableLabel =
    !currentInspectionDate || !!selectedDate?.equals(earliestSlot);
  const {
    assertions: { isInPersonSseaEnabled },
  } = useExperimentationContext();

  return (
    <AppointmentDateCardWithoutContext
      displayTime={displayTime}
      hasUpcomingEA={hasUpcomingEA}
      isSelecting={isSelecting}
      shouldShowEarliestAvailableLabel={shouldShowEarliestAvailableLabel}
      isInPersonSseaEnabled={isInPersonSseaEnabled}
      zoneId={zoneId}
      {...props}
    />
  );
};
